import React, { useRef, useState, useEffect, useCallback } from 'react';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const TabsNav = ({ tabs, className }) => {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftState, setScrollLeftState] = useState(0);
  const [dragDistance, setDragDistance] = useState(0);

  // Asegura que el tab activo esté siempre visible (Solo para Mobile/Tablet si no envuelve)
  const scrollActiveTabIntoView = useCallback(() => {
    if (!scrollRef.current || window.innerWidth >= 768) return;
    
    const activeTab = scrollRef.current.querySelector('[data-state="active"]');
    if (activeTab) {
      const container = scrollRef.current;
      const tabRect = activeTab.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      if (tabRect.left < containerRect.left) {
        container.scrollBy({ left: tabRect.left - containerRect.left - 20, behavior: 'smooth' });
      } 
      else if (tabRect.right > containerRect.right) {
        container.scrollBy({ left: tabRect.right - containerRect.right + 20, behavior: 'smooth' });
      }
    }
  }, []);

  useEffect(() => {
    // Escuchar cuando cambia el tab activo para centrarlo (solo en mobile)
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-state') {
          scrollActiveTabIntoView();
        }
      });
    });

    if (scrollRef.current) {
      mutationObserver.observe(scrollRef.current, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-state']
      });
    }

    return () => {
      mutationObserver.disconnect();
    };
  }, [tabs, scrollActiveTabIntoView]);

  // Draggable logic (Solo Mobile/Tablet para facilitar el swipe con mouse en simulador o dispositivos táctiles)
  const onMouseDown = (e) => {
    if (window.innerWidth >= 768) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeftState(scrollRef.current.scrollLeft);
    setDragDistance(0);
  };

  const onMouseUp = () => setIsDragging(false);
  const onMouseLeave = () => setIsDragging(false);

  const onMouseMove = (e) => {
    if (!isDragging || window.innerWidth >= 768) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeftState - walk;
    setDragDistance(Math.abs(walk));
  };

  return (
    <div className={cn("relative w-full select-none mb-4", className)}>
      {/* Área de Tabs - Botones de navegación ELIMINADOS por petición del usuario */}
      <div 
        ref={scrollRef}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        className="premium-tabs-scroll-area no-scrollbar scroll-smooth overflow-x-auto md:overflow-visible cursor-grab active:cursor-grabbing md:cursor-default"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <TabsList className="premium-tabs-list h-auto">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              data-testid={tab.testId}
              className={cn(
                "premium-tab-trigger",
                isDragging && "pointer-events-none"
              )}
              onClick={(e) => {
                if (window.innerWidth < 768 && dragDistance > 10) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              <div className="flex items-center gap-2">
                {tab.label}
                {tab.badge && (
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white/20 rounded-full font-black">
                    {tab.badge}
                  </span>
                )}
              </div>
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
    </div>
  );
};

export default TabsNav;
